import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { get } from 'ctm-util-functions';

import { ErrorMessage } from './error_message';
import { SimpleFlexbox } from '../flexbox/flexbox';
import styles from './error.css';

/**
 * Shorten a word to the number opf characters specified by maxLen
 * @param  {String} word        The word to shorten
 * @param  {Number} [maxLen=40] The max length of the shortend word
 * @returns {String}             The shortened word
 */
const wordShorten = (word, maxLen = 40) => {
    return `${word.slice(0, maxLen)}...`;
};

/**
 * ServerError component for rendering a document body that displays details about
 * an error thrown due to a bad request to or response from the backend API.
 * @returns {React.Component} Server Error Component
 */
class _ServerError extends Component {

    constructor(props) {
        super(props);
        this.defaultErrorResponse = {
            config: { data: JSON.stringify({ control: {} }) },
            data: { control: { errors: [] } },
            status: 500,
            statusText: 'Internal Server Error'
        };
    }

    /**
     * Render certain information in development environments
     * @param {Object} response The response object, if its a server error
     * @param {String} data JSON data
     * @returns {Node} Development info
     */
    printDevelopmentInfo = (response, data) => {
        const errorMessages = this.getErrorMessages(response);
        const rows = [];

        rows.push(
            <SimpleFlexbox key="serverError.response" sm={6}>
                <h3>The Server responded with:</h3>
                <br />
                <b>
                    {`Status: ${response.status}`}
                </b>
                <ul>
                    {errorMessages.map((msg) => <li key={msg}>{msg}</li>)}
                </ul>
            </SimpleFlexbox>
        );
        rows.push(
            <SimpleFlexbox key="serverError.payload" sm={6}>
                <h3>Response Payload</h3>
                <pre className={styles.json_pre}>
                    <code>
                        {JSON.stringify(data, null, 2)}
                    </code>
                </pre>
            </SimpleFlexbox>
        );
        return <SimpleFlexbox row="sm" column="xs" display="flex">{rows}</SimpleFlexbox>;
    };

    /**
     * Get the error messages.
     * @param {Object} response The response object, if its a server error.
     * @returns {Array} An array of error messages.
     */
    getErrorMessages = (response) => {
        let errorMessages = [];

        // this should handle 400, 401, 403, 404, 405, 500, 501, 502 and 503 errors
        // I'm not familiar enough with errors >405 <500 and >503 but this *should* handle those as well
        try {
            errorMessages = response.data.control.errors.reduce((prev, next) => {
                prev.push(`${response.data.path || response.request.responseURL} - ${response.data.error || next.errorMessage}`);
                return prev;
            }, []);
        } catch (e) {
            errorMessages = [`Service Response: ${response.statusText || response.status}`];
        }

        return errorMessages;
    };

    /**
     * Get the development Info and error response details.
     * @param {Object} response The response object, if its a server error.
     * @returns {Array} An array of JSX containing the error messages.
     */
    getDevelopmentInfo = (response) => {
        // defensive coding -- certain errors will return data as a string, maybe a null.
        if (typeof response.data !== 'object' || response.data === null) {
            // eslint-disable-next-line no-param-reassign
            response.data = { control: {} };
        }
        // Set default config data it not set in the response.
        if (typeof response.config !== 'object' || response.config === null) {
            // eslint-disable-next-line no-param-reassign
            response.config = { data: '{"control":{}}' };
        }

        // configData is always a JSON string
        // eslint-disable-next-line quotes
        const configData = JSON.parse(get(response, 'config.data', "{}"));

        // Set the data to be shown on
        const data = get(response, 'data.control.errors') || response.data || configData;

        if (data.control && data.control.transactionChecksum) {
            // Shorten the transaction checksum if present as this can throw out the page layout
            data.control.transactionChecksum = wordShorten(data.control.transactionChecksum);
        }
        return this.printDevelopmentInfo(response, data);
    };

    /**
     * Returns a FlexBox layout with details of the server error
     * @returns {Node} The rendered page
     */
    render = () => {
        const { error, dispatch } = this.props;
        const response = get(error, 'response', this.defaultErrorResponse);
        const developmentInfo = process.env.NODE_ENV === 'development' ? this.getDevelopmentInfo(response) : null;

        return (
            <>
                <ErrorMessage error={error} dispatch={dispatch} response={response} />
                {developmentInfo}
            </>
        );
    };

}

_ServerError.propTypes = {
    dispatch: PropTypes.func.isRequired,
    error: PropTypes.object.isRequired
};

const mapStateToProps = (state, props) => {
    return {
        error: state.getIn(['error', 'serverError'])
    };
};

const ServerError = connect(mapStateToProps)(_ServerError);
export { _ServerError, ServerError };
